import { useFacets } from "../../hooks/useFacets";
import { Form } from "formik";
import Button, { BUTTON_VARIANTS, BUTTON_SIZES } from "app/pages/.shared/form/Button";
import { FormattedMessage } from "react-intl";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import IconBack from "app/pages/.shared/static/icons/IconBack";
import { FilterCollapsible } from "../mobile/FilterCollapsible";
import Separator from "app/pages/.shared/Separator";
import { Fragment, useState } from "react";
import { FilterChip } from "./FilterChip";
import { useFilterContext } from "../../contexts/FilterProvider";
import PropTypes from "prop-types";
import { FILTER_SELECTION_TYPES } from "../../constants/filterSelectionTypes";
import { FilterDrawerFormPanel } from "./FilterDrawerFormPanel";
import "./FilterDrawerForm.scss";

export const FilterDrawerForm = ({
	isSinglePanelMode,
	selectedFilters,
	onReset,
	onChange,
	onApply,
}) => {
	const { closeDrawer } = useFilterContext();
	const { facets, filteredProducts } = useFacets(selectedFilters);

	const resultsCount = filteredProducts?.length;

	const expanded = [Object.keys(facets)[0] || null];
	const [expandedPanels, setExpandedPanels] = useState(isSinglePanelMode ? null : expanded);

	const hasSelectedFilters = Object.values(selectedFilters).some(
		values => Array.isArray(values) && values.length > 0
	);

	const selectedOptionsByFacet = Object.fromEntries(
		Object.entries(facets).map(([facetKey, facetCfg]) => {
			const ids = new Set(selectedFilters?.[facetKey] ?? []);
			const opts = (facetCfg.options ?? []).filter(o => ids.has(o.value));
			return [facetKey, opts];
		})
	);

	const hasMultiSelection = Object.values(facets).some(
		facet => facet.selectionMode === FILTER_SELECTION_TYPES.MULTIPLE
	);

	const handlePanelChange = key => {
		if (isSinglePanelMode) {
			setExpandedPanels(prevKey => (prevKey === key ? null : key));
		} else {
			const keys = Array.isArray(key) ? key : [key];
			setExpandedPanels(keys);
		}
	};

	const handleFilterValueChange = (filterField, value) => {
		const newValue = Array.isArray(value) ? value : [value];
		onChange(filterField, newValue);
	};

	const handleDeleteFilterOption = (filterField, value) => {
		const currentValues = selectedFilters?.[filterField] || [];
		const newValues = currentValues.filter(currentValue => currentValue !== value);
		onChange(filterField, newValues);
	};

	const handleApply = () => {
		if (typeof onApply === "function") {
			onApply(selectedFilters, resultsCount);
		}
	};

	return (
		<Form className="filter-drawer-form">
			<header className="filter-drawer-form__header">
				<Button
					onClick={closeDrawer}
					icon={<IconBack size={20} />}
					className="filter-drawer-form__close-button"
				/>

				<Typography
					variant={TYPOGRAPHY_VARIANTS.XL}
					component="h2"
					isBold
					className="filter-drawer-form__title"
				>
					<FormattedMessage id="filter.mobile.button.label" />
				</Typography>

				{hasSelectedFilters && (
					<Button
						onClick={onReset}
						className="filter-drawer-form__reset-button"
						variant={BUTTON_VARIANTS.BORDERLESS}
					>
						<Typography
							variant={TYPOGRAPHY_VARIANTS.REGULAR}
							component="span"
							isBold
							className="filter-drawer-form__reset-button"
						>
							<FormattedMessage id="general.filter.raz.label" />
						</Typography>
					</Button>
				)}
			</header>

			<section className="filter-drawer-form__body">
				{hasSelectedFilters && (
					<>
						<ul className="filter-drawer-form__selected-filters">
							{Object.entries(selectedOptionsByFacet).map(
								([selectedFilter, selectedValues]) => {
									return selectedValues.map(({ label, value }) => {
										if (!label || !value) {
											return null;
										}

										return (
											<li
												key={`${selectedFilter}-${value}`}
												className="filter-drawer-form__selected-filter"
											>
												<FilterChip
													label={label}
													onRemove={() =>
														handleDeleteFilterOption(
															selectedFilter,
															value
														)
													}
												/>
											</li>
										);
									});
								}
							)}
						</ul>

						<Separator design="full" />
					</>
				)}

				<FilterCollapsible
					accordion={isSinglePanelMode}
					activeKey={expandedPanels}
					onChange={handlePanelChange}
				>
					{Object.entries(facets).map(([filterField, facet], index) => {
						const selectedOptions = selectedOptionsByFacet?.[filterField] || [];

						return (
							<Fragment key={filterField}>
								<FilterDrawerFormPanel
									key={filterField}
									filterField={filterField}
									options={facet.options}
									selectedOptions={selectedOptions}
									panelTitle={facet.title}
									selectionMode={facet.selectionMode}
									onFilterValueChange={handleFilterValueChange}
									shouldDisplaySearchbar={facet.isSearchable}
									searchbarPlaceholderKey={facet?.search?.placeholderKey}
									showSelectedFiltersCount={hasMultiSelection}
								/>

								{index < Object.entries(facets).length - 1 && (
									<Separator design="full" />
								)}
							</Fragment>
						);
					})}
				</FilterCollapsible>
			</section>

			<footer className="filter-drawer-form__footer">
				<Button
					type="submit"
					className="filter-drawer-form__submit-button"
					size={BUTTON_SIZES.LARGE}
					onClick={handleApply}
					data-testid="filter-drawer-form-apply-button"
				>
					<Typography
						variant={TYPOGRAPHY_VARIANTS.LARGE}
						component="span"
						isBold
						className="filter-drawer-form__submit-text"
					>
						{resultsCount ? (
							<FormattedMessage
								id="product.filter.submit"
								values={{ count: resultsCount }}
							/>
						) : (
							<FormattedMessage id="product.page.filter.validate.button.labels.plural" />
						)}
					</Typography>
				</Button>
			</footer>
		</Form>
	);
};

FilterDrawerForm.propTypes = {
	isSinglePanelMode: PropTypes.bool.isRequired,
	selectedFilters: PropTypes.objectOf(PropTypes.array).isRequired,
	onReset: PropTypes.func.isRequired,
	onChange: PropTypes.func.isRequired,
	onApply: PropTypes.func,
};
